/**
 * OnboardingWizard
 * Main multi-step wizard container for provider onboarding.
 * Manages step navigation, data persistence (localStorage), validation,
 * and final submission via POST /api/provider-portal/onboarding.
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import OnboardingStepIndicator from '../../components/OnboardingStepIndicator';
import Step1BusinessInfo, { validateStep1 } from './Step1BusinessInfo';
import Step2ContactDetails, { validateStep2, getDefaultStep2Data } from './Step2ContactDetails';
import Step3ServiceAreas, { validateStep3 } from './Step3ServiceAreas';
import Step4Pricing, { validateStep4 } from './Step4Pricing';
import Step5Verification, { validateStep5 } from './Step5Verification';
import Step6ReviewSubmit, { validateStep6 } from './Step6ReviewSubmit';
import api from '../../api';

const STORAGE_KEY = 'sm_compare_onboarding';
const TOTAL_STEPS = 6;

const STEP_LABELS = [
  'Business Information',
  'Contact & Online Presence',
  'Service Coverage',
  'Pricing',
  'Verification & Compliance',
  'Review & Submit',
];

function loadSavedData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // Ignore parse errors
  }
  return null;
}

function saveData(data) {
  try {
    // Strip File objects before saving (they can't be serialized)
    const sanitized = JSON.parse(JSON.stringify(data, (key, val) => {
      if (val instanceof File) return undefined;
      return val;
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
  } catch {
    // Ignore storage errors
  }
}

function getDefaultData() {
  return {
    step1: {
      businessName: '',
      legalName: '',
      businessNumber: '',
      sector: '',
      province: '',
      city: '',
      yearEstablished: '',
      employeeRange: '',
      description: '',
      logoFile: null,
    },
    step2: getDefaultStep2Data(),
    step3: {
      provinces: [],
      cities: {},
      serviceRadius: '',
      remoteServices: false,
      languages: [],
    },
    step4: {
      plans: [],
      pricingPledge: false,
    },
    step5: {
      isedBusinessNumber: '',
      provincialLicense: '',
      bbbAccredited: false,
      bbbId: '',
      certifications: [],
      hasInsurance: false,
      insuranceProvider: '',
      insurancePolicyNumber: '',
      businessLicenseFile: null,
      insuranceCertFile: null,
      privacyPolicyUrl: '',
      termsUrl: '',
    },
    step6: {
      agreeTerms: false,
      certifyAccurate: false,
    },
    completedSteps: [],
  };
}

const VALIDATORS = {
  1: validateStep1,
  2: validateStep2,
  3: validateStep3,
  4: validateStep4,
  5: validateStep5,
  6: validateStep6,
};

export default function OnboardingWizard() {
  const { step: stepParam } = useParams();
  const navigate = useNavigate();
  const currentStep = Math.max(1, Math.min(TOTAL_STEPS, parseInt(stepParam, 10) || 1));

  // Load persisted data
  const [allData, setAllData] = useState(() => {
    const saved = loadSavedData();
    return saved ? { ...getDefaultData(), ...saved } : getDefaultData();
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const completedSteps = allData.completedSteps || [];

  // Persist data on changes
  useEffect(() => {
    saveData(allData);
  }, [allData]);

  // Get current step's data key
  const stepKey = `step${currentStep}`;
  const stepData = allData[stepKey] || {};

  // Update step data
  const handleStepDataChange = useCallback((newStepData) => {
    setAllData((prev) => ({
      ...prev,
      [stepKey]: newStepData,
    }));
  }, [stepKey]);

  // Field blur handler for per-field validation
  const handleBlur = useCallback((fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const validator = VALIDATORS[currentStep];
    if (validator) {
      const stepErrors = validator(allData[stepKey] || {});
      setErrors(stepErrors);
    }
  }, [currentStep, allData, stepKey]);

  // Validate current step
  const validateCurrentStep = useCallback(() => {
    const validator = VALIDATORS[currentStep];
    if (!validator) return {};
    const stepErrors = validator(allData[stepKey] || {});
    setErrors(stepErrors);
    // Mark all fields as touched on navigation attempt
    const allTouched = {};
    Object.keys(stepErrors).forEach((key) => { allTouched[key] = true; });
    setTouched(allTouched);
    return stepErrors;
  }, [currentStep, allData, stepKey]);

  // Mark step as completed
  const markStepCompleted = useCallback((step) => {
    setAllData((prev) => {
      const completed = prev.completedSteps || [];
      if (completed.includes(step)) return prev;
      return { ...prev, completedSteps: [...completed, step] };
    });
  }, []);

  // Navigate to step
  const goToStep = useCallback((step) => {
    setErrors({});
    setTouched({});
    navigate(`/onboarding/step/${step}`);
  }, [navigate]);

  // Next step
  const handleNext = useCallback(() => {
    const stepErrors = validateCurrentStep();
    if (Object.keys(stepErrors).length > 0) return;
    markStepCompleted(currentStep);
    if (currentStep < TOTAL_STEPS) {
      goToStep(currentStep + 1);
    }
  }, [validateCurrentStep, markStepCompleted, currentStep, goToStep]);

  // Back step
  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  // Skip step (mark NOT completed but move forward)
  const handleSkip = useCallback(() => {
    if (currentStep < TOTAL_STEPS) {
      goToStep(currentStep + 1);
    }
  }, [currentStep, goToStep]);

  // Step click on indicator
  const handleStepClick = useCallback((step) => {
    goToStep(step);
  }, [goToStep]);

  // Handle edit from review page
  const handleEditFromReview = useCallback((step) => {
    goToStep(step);
  }, [goToStep]);

  // Submit onboarding
  const handleSubmit = useCallback(async () => {
    // Validate step 6
    const reviewErrors = validateStep6(allData.step6 || {});
    setErrors(reviewErrors);
    if (Object.keys(reviewErrors).length > 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Prepare payload (strip File objects, send as metadata)
      const payload = {
        businessInfo: { ...allData.step1 },
        contactDetails: { ...allData.step2 },
        serviceAreas: { ...allData.step3 },
        pricing: { ...allData.step4 },
        verification: { ...allData.step5 },
        agreements: { ...allData.step6 },
      };

      // Remove File objects from payload (those would be uploaded separately)
      delete payload.businessInfo.logoFile;
      delete payload.verification.businessLicenseFile;
      delete payload.verification.insuranceCertFile;

      await api.post('/api/provider-portal/onboarding/submit', payload);

      // Clear saved data
      localStorage.removeItem(STORAGE_KEY);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [allData]);

  // Success screen
  if (submitted) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '500px',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          {/* Success animation placeholder */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#f0fdf4',
            border: '3px solid #86efac',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            animation: 'successPop 0.5s ease-out',
          }}>
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <path d="M8 18.5L15 25.5L28 10.5" stroke="#22c55e" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>
            Application Submitted!
          </h2>
          <p style={{ fontSize: '15px', color: '#475569', lineHeight: '1.6', marginBottom: '16px' }}>
            Thank you for applying to be listed on SM-Compare. Our team will review your
            application and verify your information.
          </p>

          <div style={{
            padding: '16px',
            backgroundColor: '#f0f9ff',
            borderRadius: '10px',
            border: '1px solid #bfdbfe',
            marginBottom: '24px',
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
              Estimated Review Timeline
            </div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
              2-5 Business Days
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
              You will receive an email notification once your application has been reviewed.
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Go to Dashboard
            </button>
          </div>

          <style>{`
            @keyframes successPop {
              0% { transform: scale(0.3); opacity: 0; }
              50% { transform: scale(1.1); }
              100% { transform: scale(1); opacity: 1; }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Calculate progress
  const progressPercent = Math.round((completedSteps.length / TOTAL_STEPS) * 100);

  // Is current step skippable? Steps 1 and 6 are not skippable
  const isSkippable = currentStep !== 1 && currentStep !== 6;

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
          Provider Onboarding
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Complete the steps below to set up your provider listing on SM-Compare.
        </p>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginTop: '12px',
        }}>
          <div style={{
            flex: 1,
            height: '6px',
            backgroundColor: '#e2e8f0',
            borderRadius: '3px',
            overflow: 'hidden',
          }}>
            <div style={{
              height: '100%',
              width: `${progressPercent}%`,
              backgroundColor: progressPercent === 100 ? '#22c55e' : '#3b82f6',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
            }} />
          </div>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap' }}>
            {progressPercent}% complete
          </span>
        </div>
      </div>

      {/* Step indicator */}
      <OnboardingStepIndicator
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepClick={handleStepClick}
      />

      {/* Current step label */}
      <div style={{
        fontSize: '12px',
        fontWeight: '600',
        color: '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '8px',
      }}>
        Step {currentStep} of {TOTAL_STEPS}
      </div>

      {/* Step content */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '24px',
        marginBottom: '24px',
      }}>
        {currentStep === 1 && (
          <Step1BusinessInfo
            data={stepData}
            onChange={handleStepDataChange}
            errors={errors}
            touched={touched}
            onBlur={handleBlur}
          />
        )}
        {currentStep === 2 && (
          <Step2ContactDetails
            data={stepData}
            onChange={handleStepDataChange}
            errors={errors}
            touched={touched}
            onBlur={handleBlur}
          />
        )}
        {currentStep === 3 && (
          <Step3ServiceAreas
            data={stepData}
            onChange={handleStepDataChange}
            errors={errors}
          />
        )}
        {currentStep === 4 && (
          <Step4Pricing
            data={stepData}
            onChange={handleStepDataChange}
            errors={errors}
          />
        )}
        {currentStep === 5 && (
          <Step5Verification
            data={stepData}
            onChange={handleStepDataChange}
            errors={errors}
            touched={touched}
            onBlur={handleBlur}
          />
        )}
        {currentStep === 6 && (
          <Step6ReviewSubmit
            allData={allData}
            reviewData={stepData}
            onChange={handleStepDataChange}
            errors={errors}
            onEdit={handleEditFromReview}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        )}
      </div>

      {/* Navigation buttons */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        flexWrap: 'wrap',
      }}>
        {/* Left side: Back */}
        <div>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handleBack}
              style={{
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: '#fff',
                color: '#475569',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Back
            </button>
          )}
        </div>

        {/* Right side: Skip + Next */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {isSkippable && (
            <button
              type="button"
              onClick={handleSkip}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                backgroundColor: '#fff',
                color: '#94a3b8',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Skip
            </button>
          )}
          {currentStep < TOTAL_STEPS && (
            <button
              type="button"
              onClick={handleNext}
              style={{
                padding: '10px 28px',
                fontSize: '14px',
                fontWeight: '700',
                backgroundColor: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
